import type { CurvaDeForca } from '../../dominio/tabletop'

const virgula = (valor: number, casas = 1) => valor.toFixed(casas).replace('.', ',')

const LARGURA = 320
const ALTURA = 150
const MARGEM = { esquerda: 34, direita: 10, topo: 12, base: 26 } as const

/** Um passo "redondo" para as marcas do eixo (1, 2 ou 5 vezes uma potência de 10). */
function passoRedondo(maximo: number, marcas: number): number {
  const bruto = maximo / marcas
  const potencia = 10 ** Math.floor(Math.log10(bruto))
  const fator = bruto / potencia
  return (fator <= 1 ? 1 : fator <= 2 ? 2 : fator <= 5 ? 5 : 10) * potencia
}

/**
 * A força no corpo do instante em que o talabarte estica até o ponto mais
 * baixo: a curva, a linha do limite de referência, a do peso e o pico marcado.
 * `tempoS` (desde o talabarte esticar) põe o cursor da régua de tempo.
 */
export function GraficoForca({
  curva,
  pesoKN,
  limiteKN,
  tempoS,
  referencia,
}: {
  curva: CurvaDeForca
  pesoKN: number
  limiteKN: number
  tempoS: number | null
  /** A curva da queda guardada para comparar: mesmos eixos, tracejada. */
  referencia?: CurvaDeForca
}) {
  const duracaoMs = Math.max(curva.duracaoS * 1000, (referencia?.duracaoS ?? 0) * 1000, 1)
  const maxY = Math.max(curva.picoKN, referencia?.picoKN ?? 0, limiteKN) * 1.15
  const largura = LARGURA - MARGEM.esquerda - MARGEM.direita
  const altura = ALTURA - MARGEM.topo - MARGEM.base
  const x = (ms: number) => MARGEM.esquerda + (ms / duracaoMs) * largura
  const y = (kN: number) => MARGEM.topo + altura - (kN / maxY) * altura

  const desenhar = (c: CurvaDeForca) => c.pontos.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t * 1000).toFixed(1)},${y(p.forcaKN).toFixed(1)}`).join(' ')
  const caminho = desenhar(curva)
  const picoDe = (c: CurvaDeForca) => c.pontos.reduce((melhor, p) => (p.forcaKN > melhor.forcaKN ? p : melhor), c.pontos[0]!)
  const picoRef = referencia ? picoDe(referencia) : null
  const pico = curva.pontos.reduce((melhor, p) => (p.forcaKN > melhor.forcaKN ? p : melhor), curva.pontos[0]!)
  const passoY = passoRedondo(maxY, 4)
  const marcasY = Array.from({ length: Math.floor(maxY / passoY) + 1 }, (_, i) => i * passoY)
  const passoX = passoRedondo(duracaoMs, 4)
  const marcasX = Array.from({ length: Math.floor(duracaoMs / passoX) + 1 }, (_, i) => i * passoX)
  const cursorMs = tempoS === null ? null : Math.min(duracaoMs, Math.max(0, tempoS * 1000))

  return (
    <figure className="grafico-forca">
      <svg
        viewBox={`0 0 ${LARGURA} ${ALTURA}`}
        role="img"
        aria-label={`Força no tempo: pico de ${virgula(curva.picoKN)} kN aos ${Math.round(pico.t * 1000)} ms; a queda para em ${Math.round(duracaoMs)} ms. Limite de referência ${virgula(limiteKN, 0)} kN.${referencia ? ` Comparada com a queda de referência: pico de ${virgula(referencia.picoKN)} kN, para em ${Math.round(referencia.duracaoS * 1000)} ms.` : ''}`}
      >
        {marcasY.map((v) => (
          <g key={`y${v}`}>
            <line className="grafico-forca__grade" x1={MARGEM.esquerda} x2={LARGURA - MARGEM.direita} y1={y(v)} y2={y(v)} />
            <text className="grafico-forca__marca" x={MARGEM.esquerda - 4} y={y(v) + 3} textAnchor="end">
              {virgula(v, passoY < 1 ? 1 : 0)}
            </text>
          </g>
        ))}
        {marcasX.map((v) => (
          <text key={`x${v}`} className="grafico-forca__marca" x={x(v)} y={ALTURA - MARGEM.base + 12} textAnchor="middle">
            {Math.round(v)}
          </text>
        ))}
        <text className="grafico-forca__marca" x={LARGURA - MARGEM.direita} y={ALTURA - 2} textAnchor="end">
          ms desde o talabarte esticar
        </text>
        <text className="grafico-forca__marca" x={2} y={MARGEM.topo - 3}>
          kN
        </text>

        <line className="grafico-forca__limite" x1={MARGEM.esquerda} x2={LARGURA - MARGEM.direita} y1={y(limiteKN)} y2={y(limiteKN)} />
        {/* à esquerda: o pico costuma ficar à direita, perto do fim */}
        <text className="grafico-forca__rotulo grafico-forca__rotulo--limite" x={MARGEM.esquerda + 3} y={y(limiteKN) - 3}>
          limite {virgula(limiteKN, 0)} kN
        </text>
        <line className="grafico-forca__peso" x1={MARGEM.esquerda} x2={LARGURA - MARGEM.direita} y1={y(pesoKN)} y2={y(pesoKN)} />
        <text className="grafico-forca__rotulo" x={LARGURA - MARGEM.direita - 2} y={y(pesoKN) - 3} textAnchor="end">
          peso
        </text>

        {referencia && picoRef && (
          <>
            <path className="grafico-forca__curva grafico-forca__curva--referencia" d={desenhar(referencia)} data-referencia />
            <circle className="grafico-forca__pico grafico-forca__pico--referencia" cx={x(picoRef.t * 1000)} cy={y(picoRef.forcaKN)} r={3} />
          </>
        )}
        <path className="grafico-forca__curva" d={caminho} />
        <circle className="grafico-forca__pico" cx={x(pico.t * 1000)} cy={y(pico.forcaKN)} r={3.5} />
        <text
          className="grafico-forca__rotulo grafico-forca__rotulo--pico"
          x={Math.min(x(pico.t * 1000) + 5, LARGURA - MARGEM.direita - 40)}
          y={Math.max(y(pico.forcaKN) - 5, MARGEM.topo + 8)}
        >
          pico {virgula(curva.picoKN)} kN
        </text>

        {cursorMs !== null && (
          <line className="grafico-forca__cursor" x1={x(cursorMs)} x2={x(cursorMs)} y1={MARGEM.topo} y2={MARGEM.topo + altura} data-cursor />
        )}
      </svg>
    </figure>
  )
}
